import { reportError } from "@cabocheck/shared";
import { client } from "~/client/client";
import { staticBaseParams } from "~/utils/getBaseParams";
import { getDeviceLocale } from "~/utils/getDeviceLocale";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import type { Route } from "./+types/_pages.agb";
import { useLoaderData } from "react-router";

export async function loader({ request }: Route.LoaderArgs) {
  try {
    const deviceLocale = getDeviceLocale(request);
    const baseParams = {
      ...staticBaseParams,
      device_locale: deviceLocale,
    };
    const response = await client.v1.getApiAgbDocumentPublicCurrent(baseParams);
    return {
      agbDocuments: response.data.response?.agb_documents ?? [],
    };
  } catch (error) {
    reportError("Failed to load AGB documents", error);
    return {
      agbDocuments: [],
    };
  }
}

export default function Datenschutz() {
  const { t } = useTranslation();
  const { agbDocuments } = useLoaderData<typeof loader>();

  const productTypeLabels: Record<string, string> = {
    CaboLAB: t("CaboLAB"),
    CaboVAX: t("CaboVAX"),
    CaboPOC: t("CaboPOC"),
  };

  return (
    <div id="RootRoot" className="flex w-full flex-col justify-end">
      <h1 className="py-16">AGB</h1>
      <b className="">Allgemeine Geschäftsbedingungen (AGB) für den Dienst „CaboCheck" gegenüber Endverbrauchern</b>

      {agbDocuments.length > 0 ? (
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          {(["CaboLAB", "CaboVAX", "CaboPOC"] as const).map((productType) => {
            const agbDoc = agbDocuments.find((doc) => doc.product_type === productType);

            if (!agbDoc) {
              return null;
            }

            return (
              <div key={productType} className="rounded-lg border border-gray-200 p-6">
                <h3 className="mb-4 text-lg font-semibold">{productTypeLabels[productType]}</h3>
                <div className="space-y-4">
                  {agbDoc.thumbnail ? (
                    <div className="relative h-48 w-full overflow-hidden rounded-md bg-gray-100">
                      <img
                        src={agbDoc.thumbnail}
                        alt={`${productTypeLabels[productType]} AGB`}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ) : null}
                  <a
                    href={agbDoc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                  >
                    <Download className="h-4 w-4" />
                    {t("PDF herunterladen")}
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      <ol className="mt-10 grid gap-12">
        <li className="grid gap-4">
          <h3>1. Anwendungsbereich</h3>
          <p>
            1.1 Die geschäftliche Tätigkeit der HEALYZER Health Analyzing Technologies GmbH, Zimmerpforte 3, 20099
            Hamburg, E-Mail: datenschutz@healyzer.com (nachfolgend „HAT" genannt) besteht in der Entwicklung und dem
            Angebot technischer Lösungen und Dienstleistungen zur RILI-BäK konformen Durchführung und Nutzung von
            PoC-PCR Maschinen in stationären Apotheken und sonstigen Gesundheitseinrichtungen sowie der Ermöglichung der
            Durchführung diverser Gesundheitstests in Zusammenarbeit mit stationären Apotheken und medizinischen Laboren
            (nachfolgend „Labor" genannt).
          </p>

          <p>
            HEALYZER betreibt einen Dienst mit dem Namen „CaboCheck", der als Webanwendung unter der URL www.cabocheckde
            („CaboCheck-Webseite") erreichbar ist. Mit CaboCheck können Nutzer diverse Gesundheitstests in Anspruch
            nehmen. Die Probenentnahme findet dabei in einer teilnehmenden stationären Apotheke unter Beachtung der
            fachspezifischen Standards statt, während die Auswertung des Tests von einem zertifizierten, von einem
            Facharzt geleiteten Labor durchgeführt wird.
          </p>
          <p>
            Der Apotheker verkauft den Test in seiner stationären Apotheke und nimmt die Probenentnahme vor. Er
            vermittelt zudem den Vertragsschluss mit HEALYZER.
          </p>

          <p>
            HEALYZER stellt die technische Infrastruktur zur Verfügung, ermöglicht die Probenahme in den teilnehmenden
            Apotheken, stellt die Probenauswertung in den teilnehmenden Laboren sicher, erbringt die Abwicklung der
            Buchung und Bezahlung der Durchführung des Gesundheitstests über die CaboCheck-Webseite und ermöglicht dem
            Kunden den Abruf des Ergebnisses der Testauswertung über die CaboCheck-Webseite.
          </p>
          <p>
            HEALYZER arbeitet dabei mit unterschiedlichen zertifizierten Facharztlaboren in Deutschland, welche die
            eigentliche Laboranalyse durchführen und bei einem positiven Testergebnis etwaige Meldepflichten nach dem
            Infektionsschutzgesetz (IfSG) erfüllen werden.
          </p>
          <p>
            HEALYZER kann eines der Labore mit der Durchführung des Gesundheitstests beauftragen. Der Kunde hat insoweit
            kein Bestimmungsrecht. Die Bestimmung des Labors richtet sich nach der Testung und der Kapazität. Ein
            Qualitätsunterschied zwischen den Laboren ist nicht gegeben.
          </p>
          <p>
            1.2 Das Angebot richtet sich an Verbraucher ab 16 Jahren. Personen unter 16 Jahren dürfen den Test nur in
            Begleitung einer erziehungsberechtigten Person nutzen.
          </p>
          <p>
            Verbraucher ist gem. § 13 BGB jede natürliche Person, die die CaboCheck-Dienstleistungen zu einem Zweck
            nutzt, der überwiegend weder ihrer gewerblichen noch selbständigen beruflichen Tätigkeit zuzurechnen ist.
            Sie können Verträge mit uns nur als volljährige natürliche Person abschließen. Jedoch können auch Eltern für
            Ihre Kinder Tests buchen und diese im Beisein durchführen.
          </p>

          <p>
            1.3 Die vorliegenden Allgemeinen Geschäftsbedingungen (AGB) sind Vertragsbestandteil und gelten für alle,
            somit auch zukünftigen Geschäftsbeziehungen zwischen HEALYZER und Ihnen im Zusammenhang mit dem Dienst
            CaboCheck. Die AGB werden von Ihnen in vollem Umfang in der zum Zeitpunkt des Vertragsschlusses geltenden
            Fassung akzeptiert. Von diesen Bedingungen abweichenden Regelungen wird hiermit widersprochen.
          </p>

          <p>1.4 Für den Vertragsschluss steht ausschließlich die deutsche Sprache zur Verfügung.</p>
          <p>
            1.5 Die Bestellabwicklung und Kontaktaufnahme finden in der Regel per E-Mail und automatisierter
            Bestellabwicklung statt. Der Kunde hat sicherzustellen, dass die von ihm zur Bestellabwicklung angegebene
            E-Mail-Adresse zutreffend ist, so dass unter dieser Adresse die von HEALYZER versandten E-Mails empfangen
            werden können. Insbesondere hat der Kunde bei dem Einsatz von SPAM- Filtern sicherzustellen, dass alle von
            der HEALYZER oder von dieser mit der Bestellabwicklung beauftragten Dritten versandten E-Mails zugestellt
            werden können.
          </p>
        </li>
        <li className="grid gap-4">
          <h3>2. Leistungsumfang</h3>

          <p>Die CaboCheck Dienstleistungen verfügen über folgenden Leistungsumfang:</p>
          <p>2.1 Test buchen und bezahlen in der Apotheke.</p>
          <p>
            Der Kunde kann in einer der teilnehmenden stationären Apotheken die Durchführung eines der dort angebotenen
            Gesundheitstests buchen und bezahlen. Der Apotheker berät den Kunden, verkauft den Test und führt die
            Probenahme durch.
          </p>

          <p>
            Der Apotheker übergibt dem Kunden als Teil des Verkaufs eine CaboCheck Broschüre (Leporello) mit einem
            QR-Code. Dieser QR-Code identifiziert den Test dieses Kunden eindeutig im CaboCheck System.
          </p>

          <p>
            Der Apotheker nimmt die für die Durchführung des Tests erforderlichen Daten digital auf. Der Kunde erhält
            über das CaboCheck-System eine Bestätigung per E-Mail, die auch die Datenschutz-Hinweise der HEALYZER
            enthält.
          </p>

          <p>
            Wenn ein Kunde einen Test für einen Minderjährigen bucht, müssen Minderjährige unter 16 Jahren zwingend in
            Begleitung eines Erziehungsberechtigten zur Probeentnahme in der stationären Apotheke erscheinen. Hat der
            Apotheker den Eindruck, dass ein Kunde im Alter von 16 bis zu 18 Jahren nicht das Einsichtsvermögen für die
            Einwilligung in Bezug auf eine eventuelle Köperverletzung (bspw. Entnahme von Kapillarblut) hat, kann er das
            Einverständnis der Eltern verlangen.
          </p>

          <p>
            Anschließend entnimmt der Apotheker selbst oder durch geschultes Personal das Blut des Kunden direkt in ein
            zum Test passendes Mikroröhrchen. Dieses Röhrchen besitzt einen eindeutigen Barcode (Sample Code). Der
            Apotheker verbindet diesen Sample Code über das CaboCheck System (die CaboCheck Web-App) mit dem Test des
            Kunden. Das Mikroröhrchen, welches das Kapillarblut enthält wird an eines der medizinischen Partnerlabore
            geschickt. Nach ca. 24-48 Stunden kann der Kunde seine Laborergebnisse in Form eines PDF-Laborberichts
            abrufen.
          </p>
        </li>
        <li className="grid gap-4">
          <h3>3. Angebote und Vertragsabschluss</h3>
          <p>3.1 Alle unsere Angebote sind stets freibleibend und unverbindlich.</p>
        </li>

        <li className="grid gap-4">
          <h3>4. Preise und Zahlung</h3>
          <p>
            4.1 Es gelten die Preise, die der Apotheker für die Testdurchführung verlangt. Dieser ist in seiner
            Preisfestsetzung frei und weist die Preise entsprechend der gesetzlichen Vorgaben aus. Dem Kunden ist
            bekannt, dass die von HAT organisierte Testung nicht von der Krankenkasse getragen oder erstattet wird. Der
            Kunde hat diese Leistungen selbst zu zahlen.
          </p>

          <p>
            4.2 Die Vergütung für den gebuchten Gesundheitstest ist mit Vertragsschluss fällig und an den Apotheker zu
            leisten.
          </p>
        </li>

        <li className="grid gap-4">
          <h3>5. Zahlungsmöglichkeiten</h3>
          <p>Es gelten die in der stationären Apotheke akzeptierten Zahlungsmöglichkeiten.</p>
        </li>

        <li className="grid gap-4">
          <h3>6. Grundsätze der Leistungserbringung, Pflichten des Kunden</h3>
          <p>
            6.1 Aussagen und Erläuterungen zu den Leistungen der HEALYZER auf der CaboCheck-Webseite oder auf unseren
            Webseiten, Social-Media-Auftritten oder sonstigen Werbematerialien verstehen sich nicht als Garantie oder
            Zusicherung einer Eigenschaft. Aussagen zu den Gesundheitstests stellen nur dann Garantien oder
            Zusicherungen im Rechtssinne dar, wenn diese ausdrücklich als „Garantie" oder „Zusicherung" bezeichnet sind.
            Aussagen zur Dauer der Befundmitteilung stellen keine Zusicherung oder Garantie dar, es handelt sich um die
            üblichen Zeitläufe zwischen Probenentnahme und Befundmitteilung. Es lassen sich aus der Dauer der Mitteilung
            keinerlei Regressansprüche herleiten. Unsere vertragliche Leistung ist nach der Labordiagnostik auch dann
            erbracht, wenn nach sachgerechter Analyse kein Befund erstellt werden kann.
          </p>

          <p>
            6.2 Wir sind berechtigt, uns im Rahmen der Leistungserbringung der Hilfe von Subunternehmern zu bedienen.
          </p>

          <p>
            6.3 Wenn ein Kunde einen Test für einen Minderjährigen bucht, müssen Minderjährige unter 14 Jahren zwingend
            in Begleitung eines Erziehungsberechtigten zur Probeentnahme in der stationären Apotheke erscheinen. Hat der
            Apotheker den Eindruck, dass ein Kunde im Alter von 16 bis zu 18 Jahren nicht das Einsichtsvermögen für die
            Einwilligung in Bezug auf eine eventuelle Köperverletzung (bspw. Entnahme von Kapillarblut) hat, kann er das
            Einverständnis der Eltern verlangen.
          </p>

          <p>
            6.4 Der Kunde sichert zu, dass die im Rahmen des Erwerbs eines Tests/ QR-Codes sowie im Rahmen der
            Durchführung eines Tests in Bezug auf einen meldepflichtigen Erreger angegebenen personenbezogenen Daten
            vollständig und richtig sind.
          </p>

          <p>
            6.5 Der Kunde bzw. die zu testende Person unterstützt uns vollumfänglich bei der Leistungserbringung.
            Insbesondere hat der Kunde bzw. die zu testende Person im Hinblick auf seinen Besuch einer Apotheke den
            Anweisungen von HEALYZER und der teilnehmenden stationären Apotheken Folge zu leisten, sowie die Vorgaben
            und Verhaltensregeln (z.B. im Hinblick auf Hygiene- und Abstandsvorgaben) einzuhalten.
          </p>

          <p>
            6.6 Der Kunde hat die gängigen Hygieneregelungen einzuhalten und sollte im Fall einer akuten Erkrankung an
            einer Infektionskrankheit oder an einer anderen akuten Krankheit die stationäre Apotheke nicht zur
            Probenahme aufsuchen.
          </p>
        </li>

        <li className="grid gap-4">
          <h3>7. Haftung und Verfügbarkeit</h3>
          <p>
            7.1 Eine Haftung von HEALYZER – gleich aus welchem Rechtsgrund – tritt nur ein, wenn der Schaden a. durch
            schuldhafte Verletzung einer wesentlichen Vertragspflicht oder wesentlichen Nebenpflichten in einer das
            Erreichen des Vertragszwecks gefährdenden Weise oder b. durch grobe Fahrlässigkeit oder Vorsatz von HEALYZER
            verursacht worden ist.
          </p>

          <p>
            7.2 Sofern wir gemäß Ziffer 7.1a) für die Verletzung einer wesentlichen Vertragspflicht haften, ohne dass
            grobe Fahrlässigkeit oder Vorsatz vorliegen, so ist die Haftung auf denjenigen Schadensumfang begrenzt, mit
            dessen Entstehen wir bei Vertragsschluss aufgrund der ihr zu diesem Zeitpunkt bekannten Umstände
            typischerweise rechnen mussten. Dies gilt in gleicher Weise für Schäden, die aufgrund von grober
            Fahrlässigkeit oder Vorsatz von unseren Mitarbeitern oder Beauftragten verursacht werden, die nicht zu den
            Geschäftsführern oder leitenden Angestellten gehören. Die Haftung für Folgeschäden, insbesondere auf
            entgangenen Gewinn oder auf Ersatz von Schäden Dritter, wird ausgeschlossen, es sei denn, es fallen uns
            Vorsatz oder grobe Fahrlässigkeit zur Last.
          </p>

          <p>
            7.3 Schadenersatzansprüche nach dem Produkthaftungsgesetz und für Schäden aus der Verletzung des Lebens, des
            Körpers oder der Gesundheit bleiben durch die vorstehenden Haftungsbeschränkungen unberührt.
          </p>

          <p>
            7.4 Die vorstehenden Haftungsbeschränkungen gelten auch zugunsten unserer eventuell eingebundenen
            gesetzlichen Vertretern und Erfüllungsgehilfen.
          </p>

          <p>
            7.5 Wir haften nicht für die Bearbeitungsdauer der Laboranalyse durch das Labor. Das Labor wird die Probe
            des Kunden schnellstmöglich und in der Regel binnen 24-72h untersuchen. Die Dauer der Laboranalyse hängt von
            dem Eingangszeitraum der Probe, sowie der Anzahl der insgesamt zu untersuchenden Proben am betreffenden Tag
            ab. Zudem ist es möglich, dass das Labor auf Anweisung des zuständigen Gesundheitsamtes einzelne Proben
            vorrangig zu bearbeiten hat.
          </p>

          <p>
            7.6 HEALYZER haftet – mit Ausnahme der Haftungsregelungen aus Ziffern 8.1-8.4 – nicht für falsche,
            unvollständige oder irreführende Laborergebnisse. Insbesondere ist eine Haftung ausgeschlossen, wenn ein
            falsches, unvollständiges oder irreführendes Laborergebnis auf einem Verschulden das Labors beruht, die
            verwendeten Testmaterialien mangelhaft waren oder sich die Ursachen eines falschen, unvollständigen oder
            irreführenden Laborergebnisses nicht ermitteln lassen.
          </p>
        </li>

        <li>
          <h3>8. Bestandskundenwerbung</h3>
          <p>
            Wir weisen darauf hin, dass die HEALYZER berechtigt ist, die, im Rahmen des Vertragsschlusses angegebene
            E-Mail-Adresse des Kunden zu verwenden, um ihm Direktwerbung für eigene ähnliche Waren oder Dienstleistungen
            zuzusenden. Der Kunde kann dieser Verwendung seiner E- Mail-Adresse jederzeit widersprechen, ohne dass
            hierfür andere als die Übermittlungskosten nach den Basistarifen entstehen. Für einen Widerspruch können Sie
            sich per Post an HEAYLZER, Health Analyzing Technologies HAT GmbH, Zimmerpforte 3, 20099 Hamburg oder per
            E-Mail: widerruf@CaboCheck.de wenden.
          </p>
        </li>
        <li>
          <h3>9. Speicherung Vertragstext</h3>

          <p>
            Der Vertragstext wird von uns nach Vertragsschluss nicht abgespeichert und dem Kunden selbst nicht
            zugänglich gemacht. Wir schicken dem Kunden aber nach Vertragsschluss eine Bestellbestätigung per E-Mail, in
            der die wesentlichen Daten zur getätigten Bestellung nochmals aufgeführt sind. Der Kunde hat die
            Möglichkeit, die Bestellung mit den von ihm/ihr eingegebenen Daten während des Bestellvorgangs auszudrucken.
          </p>
        </li>
        <li>
          <h3>10. Datenschutz</h3>
          <p>
            10.1 Im Umgang mit Ihren persönlichen Daten halten wir uns an alle Bestimmungen der
            Datenschutzgrundverordnung (DSGVO), des Bundesdatenschutzgesetzes (BDSG) und des Telemediengesetzes (TMG).
            und sind berechtigt, alle, die Geschäftsbeziehung mit Ihnen betreffenden Daten, unter Beachtung dieser
            Gesetze zu erheben, zu verarbeiten und zu nutzen.
          </p>
          <p>
            10.2 Weitere Informationen über Art, Umfang, Ort und Zweck der Erhebung, Verarbeitung und Nutzung der für
            die Ausführung von Bestellungen erforderlichen personenbezogenen Daten finden Sie in unseren
            Datenschutzbestimmungen unter https://www.healyzer.de/datenschutz/.
          </p>
        </li>
        <li>
          <p>
            11. Informationen zur alternativen Streitbeilegung Plattform der EU zur außergerichtlichen
            Online-Streitbeilegung: http://ec.europa.eu/consumers/odr/ .
          </p>
          <p>
            Für Verbraucherstreitigkeiten mit uns wäre die Verbraucherstreitbeilegungsstelle Online- Schlichter. Zentrum
            für Europäischen Verbraucherschutz e.V., Bahnhofsplatz 3, 77694 Kehl, www.online-schlichter.de zuständig.
            Wir sind jedoch nicht bereit und nicht verpflichtet, an einem Streitbeilegungsverfahren vor einer
            Verbraucherschlichtungsstelle teilzunehmen.
          </p>
        </li>
        <li>
          <h3>12. Rechtswahl, Gerichtsstand</h3>
          <p>
            Soweit gesetzlich zulässig, gilt deutsches Recht unter Ausschluss des UN-Kaufrechts (CISG). Zwingende
            Bestimmungen des Staates, in dem der Kunde seinen gewöhnlichen Aufenthalt hat, bleiben unberührt.
            Ausschließlicher Gerichtsstand für Streitigkeiten mit Kunden, die als Verbraucher handeln und keinen
            allgemeinen Gerichtsstand im Inland haben, die nach Vertragsschluss ihren Wohnsitz ins Ausland verlegen oder
            deren Wohnsitz zum Zeitpunkt der Klageerhebung nicht bekannt ist, ist an unserem jeweiligen
            Unternehmenssitz. Das Gleiche gilt bei Rechtsstreitigkeiten mit Kunden, die als Kaufleute i.S.v. § 38 ZPO
            handeln.
          </p>
        </li>
      </ol>
      <p>Stand: August 2024</p>
    </div>
  );
}
